import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RRule, RRuleSet, rrulestr, Weekday } from 'rrule';

const MIN_OFFSET = 0; // Pay on the same day or before
const DEFAULT_OFFSET = 3; // Default to 3 days before

// --- Helper Functions ---

// Safely parses an RRule string
const parseRrule = (icalString) => {
  if (!icalString) return null;
  try {
    const rule = rrulestr(icalString);
    // Handle RRuleSet if needed, for now assume single RRule
    return rule instanceof RRuleSet ? rule.rrules()[0] : rule;
  } catch (e) {
    console.error("Error parsing RRULE string:", icalString, e);
    return null;
  }
};

// Calculates the next occurrence of an RRule >= a given date
const calculateNextOccurrence = (rule, afterDate) => {
  if (!rule) return null;
  try {
    const now = afterDate || new Date();
    now.setUTCHours(0, 0, 0, 0); // Ensure comparison starts at the beginning of the day UTC
    return rule.after(now, true); // inc=true to include 'now' if it matches
  } catch (e) {
    console.error("Error calculating next occurrence:", e);
    return null;
  }
};

// Calculates the difference in days between two UTC dates
const dateDiffInDays = (date1, date2) => {
  if (!date1 || !date2) return NaN;
  const utc1 = Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate());
  const utc2 = Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate());
  return Math.floor((utc1 - utc2) / (1000 * 60 * 60 * 24));
};

// Subtracts days from a UTC date
const subtractDays = (date, days) => {
  if (!date) return null;
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() - days);
  return result;
};

// Formats a date for display
const formatDate = (date) => {
  if (!date) return 'N/A';
  return date.toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
  });
};

// Generates a readable description of the fund's schedule
const getReadableScheduleInfo = (rule) => {
  if (!rule) return "Unknown schedule";
  const options = rule.options;
  const freqMap = {
    [RRule.YEARLY]: 'Yearly',
    [RRule.MONTHLY]: 'Monthly',
    [RRule.WEEKLY]: 'Weekly',
    // Add others if needed (DAILY, HOURLY, etc.)
  };
  const weekdayMap = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']; // RRule order

  let freq = freqMap[options.freq] || 'Unknown Freq';
  let interval = options.interval > 1 ? `Every ${options.interval} ${freq.toLowerCase().replace(/ly$/, 's')}` : freq;

  let details = '';
  if (options.freq === RRule.YEARLY) {
    const month = options.bymonth && options.bymonth.length > 0 ? new Date(Date.UTC(2000, options.bymonth[0] - 1, 1)).toLocaleString('default', { month: 'long', timeZone: 'UTC' }) : '';
    const day = options.bymonthday && options.bymonthday.length > 0 ? options.bymonthday[0] : '';
    details = `on ${month} ${day}`;
  } else if (options.freq === RRule.MONTHLY) {
    if (options.bymonthday && options.bymonthday.length > 0) {
      const day = options.bymonthday[0];
      const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
      details = `on the ${day}${suffix}`;
    } else if (options.bysetpos && options.byweekday) {
      // Handle cases like "last Friday of the month" if needed
      details = "on a specific day of the month"; // Placeholder
    }
  } else if (options.freq === RRule.WEEKLY) {
    if (options.byweekday && options.byweekday.length > 0) {
      const days = options.byweekday.map(d => new Weekday(d).toString()).join(', '); // Uses RRule's formatting (e.g., MO, TU)
      details = `on ${days}`;
    }
  }

  return `${interval} ${details}`;
};

// Generate dropdown options based on the maximum allowed days
const generateOffsetOptions = (maxDays) => {
  const options = [];

  // Add "same day" option
  options.push({ value: 0, label: 'On the due date' });

  // Add options for 1 to maxDays
  for (let i = 1; i <= maxDays; i++) {
    const dayText = i === 1 ? 'day' : 'days';
    options.push({ value: i, label: `${i} ${dayText} before due date` });
  }

  return options;
};

// --- Component ---
const AutoPayOffsetSelector = ({
  fundRruleIcal,
  initialAutopayRruleIcal,
  onIcalChange,
  fieldName = 'autopay_rrule',
  invoiceGenerationLeadTimeDays = 20 // New prop for invoice generation lead time
}) => {
  const [fundRule, setFundRule] = useState(null);
  const [initialAutopayRule, setInitialAutopayRule] = useState(null);
  const [selectedOffset, setSelectedOffset] = useState(DEFAULT_OFFSET);
  const [nextFundDueDate, setNextFundDueDate] = useState(null);
  const [nextAutopayDate, setNextAutopayDate] = useState(null);
  const [fundScheduleInfo, setFundScheduleInfo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [autopayIcalString, setAutopayIcalString] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Calculate the maximum offset based on invoice generation lead time
  // Don't allow autopay to be set before invoices are generated
  const maxOffset = Math.max(0, invoiceGenerationLeadTimeDays - 1);

  // Generate dropdown options
  const offsetOptions = useMemo(() => generateOffsetOptions(maxOffset), [maxOffset]);

  // --- Initialization Effect ---
  useEffect(() => {
    // --- Add console log for debugging ---
    console.log('[AutoPayOffsetSelector] fundRruleIcal prop:', fundRruleIcal);
    console.log('[AutoPayOffsetSelector] invoiceGenerationLeadTimeDays:', invoiceGenerationLeadTimeDays);
    // --- End add console log ---

    setErrorMessage(''); // Clear previous errors
    const parsedFundRule = parseRrule(fundRruleIcal);
    if (!parsedFundRule) {
      setErrorMessage('Error: Could not understand the fund\'s schedule.');
      setIsInitialized(true);
      return;
    }

    const nextDue = calculateNextOccurrence(parsedFundRule);
    if (!nextDue) {
      setErrorMessage('Error: Could not determine the fund\'s next due date.');
      setIsInitialized(true);
      return;
    }

    setFundRule(parsedFundRule);
    setNextFundDueDate(nextDue);
    setFundScheduleInfo(getReadableScheduleInfo(parsedFundRule));

    let initialOffset = Math.min(DEFAULT_OFFSET, maxOffset); // Ensure default doesn't exceed max
    let initialIcal = '';
    const parsedInitialAutopay = parseRrule(initialAutopayRruleIcal);

    if (parsedInitialAutopay) {
      // Basic check: Ensure frequencies and intervals match before calculating offset
      if (parsedInitialAutopay.options.freq === parsedFundRule.options.freq &&
        parsedInitialAutopay.options.interval === parsedFundRule.options.interval) {
        const nextInitialAutopay = calculateNextOccurrence(parsedInitialAutopay);
        if (nextInitialAutopay) {
          const diff = dateDiffInDays(nextDue, nextInitialAutopay);
          // Only use offset if it's valid and within bounds
          if (!isNaN(diff) && diff >= MIN_OFFSET && diff <= maxOffset) {
            initialOffset = diff;
            initialIcal = initialAutopayRruleIcal; // Keep valid initial ICAL
          } else {
            console.warn("Initial autopay rule offset is invalid or out of bounds, using default.");
          }
        } else {
          console.warn("Could not determine next occurrence of initial autopay rule, using default offset.");
        }
      } else {
        console.warn("Initial autopay rule frequency/interval mismatch with fund rule, using default offset.");
      }
      setInitialAutopayRule(parsedInitialAutopay); // Store for reference if needed
    }

    setSelectedOffset(initialOffset);
    setAutopayIcalString(initialIcal); // Set initial ICAL (might be empty)
    // Trigger the update effect explicitly after setting initial state
    // Note: Direct call avoided; rely on dependency array change or a separate flag if needed.
    setIsInitialized(true);

  }, [fundRruleIcal, initialAutopayRruleIcal, maxOffset]); // Re-run if fund rule, initial autopay rule, or max offset changes


  // --- Autopay RRule Generation Effect ---
  const generateAndUpdateAutopayRule = useCallback(() => {
    if (!isInitialized || !fundRule || !nextFundDueDate || errorMessage) {
      // Don't generate if not initialized, fund rule invalid, or error exists
      setAutopayIcalString('');
      setNextAutopayDate(null);
      if (onIcalChange) onIcalChange('');
      return;
    }

    try {
      const targetAutopayDate = subtractDays(nextFundDueDate, selectedOffset);
      if (!targetAutopayDate || isNaN(targetAutopayDate.getTime())) {
        throw new Error("Calculated target autopay date is invalid.");
      }

      const fundOptions = fundRule.options;
      const autopayOptions = {
        freq: fundOptions.freq,
        interval: fundOptions.interval,
        dtstart: targetAutopayDate, // The calculated first autopay date
      };

      // Derive BY* rules based on the target date and fund frequency
      // This ensures the rule aligns with the *intended* first payment date
      const targetDayOfMonth = targetAutopayDate.getUTCDate();
      const targetMonth = targetAutopayDate.getUTCMonth() + 1; // RRule month is 1-indexed
      const targetJsDay = targetAutopayDate.getUTCDay(); // JS: Sun=0, Mon=1..Sat=6
      const targetRruleWeekday = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA][targetJsDay];

      if (fundOptions.freq === RRule.YEARLY) {
        autopayOptions.bymonth = [targetMonth];
        autopayOptions.bymonthday = [targetDayOfMonth];
      } else if (fundOptions.freq === RRule.MONTHLY) {
        // Use bymonthday based on the calculated target date
        autopayOptions.bymonthday = [targetDayOfMonth];
        // Important: Clear byweekday/bysetpos if they existed on the fund rule
        // to avoid conflicts when only bymonthday is intended for autopay.
        // Add more sophisticated handling here if fund uses complex monthly rules (e.g., last Friday)
      } else if (fundOptions.freq === RRule.WEEKLY) {
        autopayOptions.byweekday = [targetRruleWeekday];
      }
      // Add logic for other frequencies if necessary

      console.log("Generating Autopay RRule with options:", autopayOptions);
      const autopayRule = new RRule(autopayOptions);
      const newIcalString = autopayRule.toString();

      setAutopayIcalString(newIcalString);
      setNextAutopayDate(targetAutopayDate); // Display the *first* calculated date
      if (onIcalChange) {
        onIcalChange(newIcalString);
      }
      setErrorMessage(''); // Clear error on success

    } catch (e) {
      console.error("Error generating Autopay RRULE:", e);
      setErrorMessage("Error calculating autopay schedule. Please try again.");
      setAutopayIcalString('');
      setNextAutopayDate(null);
      if (onIcalChange) onIcalChange('');
    }
  }, [isInitialized, fundRule, nextFundDueDate, selectedOffset, onIcalChange, errorMessage]); // Dependencies

  // Trigger generation when relevant state changes
  useEffect(() => {
    generateAndUpdateAutopayRule();
  }, [generateAndUpdateAutopayRule]); // Depend on the memoized callback


  // --- Handlers ---
  const handleOffsetChange = (event) => {
    let newOffset = parseInt(event.target.value, 10);
    if (isNaN(newOffset)) {
      newOffset = Math.min(DEFAULT_OFFSET, maxOffset); // Or keep previous valid? Resetting might be safer.
    }
    // Clamp value within bounds
    newOffset = Math.max(MIN_OFFSET, Math.min(maxOffset, newOffset));
    setSelectedOffset(newOffset);
    // The effect [generateAndUpdateAutopayRule] will handle regeneration
  };

  // --- Render ---
  if (!isInitialized) {
    return <div>Loading schedule...</div>;
  }

  if (errorMessage) {
    return <div className="text-red-600 text-sm">{errorMessage}</div>;
  }

  if (!fundRule) {
    // This case should ideally be covered by errorMessage, but as a fallback:
    return <div className="text-orange-600 text-sm">Fund schedule not available.</div>;
  }

  return (
    <div className="space-y-3">
      <input type="hidden" name={fieldName} value={autopayIcalString} />

      <div>
        <p className="text-sm text-gray-600">
          Fund Invoicing Schedule: <span className="font-medium text-gray-800">{fundScheduleInfo}</span>
        </p>
        <p className="text-sm text-gray-600">
          Next Due Date: <span className="font-medium text-gray-800">{formatDate(nextFundDueDate)}</span>
        </p>
        <p className="text-xs text-gray-500">
          Invoices are generated {invoiceGenerationLeadTimeDays} days before the due date
        </p>
      </div>

      <div>
        <label htmlFor="autopay-offset" className="block text-sm font-medium text-gray-700 mb-2">
          When should AutoPay charge the payment method?
        </label>
        <select
          id="autopay-offset"
          name="autopay-offset-value"
          value={selectedOffset}
          onChange={handleOffsetChange}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          aria-describedby="autopay-offset-description"
        >
          {offsetOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p id="autopay-offset-description" className="mt-1 text-xs text-gray-500">
          AutoPay cannot be scheduled before invoices are generated ({invoiceGenerationLeadTimeDays} days before due date)
        </p>
      </div>

      {nextAutopayDate && (
        <p className="text-sm text-green-700">
          Next AutoPay Date: <span className="font-semibold">{formatDate(nextAutopayDate)}</span>
        </p>
      )}

    </div>
  );
};

export default AutoPayOffsetSelector;